import React, { useEffect, useState, useMemo, useCallback } from 'react';
import BaseFilterCard from './base-filter-card';
import { AiOutlineAim } from 'react-icons/ai';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCharitableOptions,
  setCharitableOptionsEnabled,
  setEducationOptions,
  setEducationOptionsEnabled,
  setEngagementOptions,
  setEngagementOptionsEnabled,
  setAttendedOptions,
  setAttendedOptionsEnabled,
  setFamilyOptions,
  setFamilyOptionsEnabled,
  setNodeOptionsFilter,
  setNodeOptionsFilterEnabled,
  setOrganizationalOptions,
  setOrgOptionsEnabled,
  setProfessionalOptions,
  setProfessionalOptionsEnabled,
  setVisibleConnections,
  setDegreesOfSeparation,
} from 'store/filters/actions';
import { setFocusedNode } from 'store/graph/actions';
import {
  familyOptions,
  organizationalOptions,
  professionalOptions,
  charitableOptions,
  educationalOptions,
  nodeOptions,
  engagementOptions,
  attendedOptions,
  customFamilyGroupingOptions,
  FamilyGroupMapping,
  GetEdgeOptions,
} from './options';
import OptionsCardWrapper from './options-card';
import styles from './styles/filterCard.module.css';
import {
  selectCharitableOptionsEnabled,
  selectEducationOptionsEnabled,
  selectFamilyOptionsEnabled,
  selectNodeOptionsEnabled,
  selectOrganizationalOptionsEnabled,
  selectProfressionalOptionsEnabled,
  selectEngagementOptionsEnabled,
  selectAttendedOptionsEnabled,
  selectShowVisibleConnections,
  selectTimeFilters,
  selectViewFilterUpdates,
  selectReset,
  selectDegreesOfSeparation,
} from 'store/filters/selectors';
import { selectStyledItems, selectNodes, selectSuggestedNodes } from 'store/graph/selectors';
import { edgeColors } from 'utils/constants';
import ReactTooltip from 'react-tooltip';
import {
  CharitableIcon,
  EducationIcon,
  FamilyIcon,
  PersonalIcon,
  ProfessionalIcon,
  EngagementIcon,
  EventIcon,
} from 'common/icon/icon';
import useComponentVisible from 'utils/hooks';
import GraphSearchControl from 'common/graph-search-control/GraphSearchControl';
import { getSearchableNodes } from 'utils/graphSearchUtils';

/**
 * Filter category configuration — drives all filter cards, dispatch, state, and reset logic.
 * Each entry defines one filter category (Family, Personal, Professional, etc.).
 *
 * Adding a new filter category:
 *   1. Add an entry here with the appropriate actions, selectors, and options
 *   2. Add the corresponding BaseFilterCard + OptionsCardWrapper in the JSX (or use the
 *      dynamic rendering below)
 */
const FILTER_CATEGORIES = [
  {
    key: 'family',
    selector: 0,
    label: 'Family',
    colorKey: 'family',
    enabledSelector: selectFamilyOptionsEnabled,
    setEnabledAction: setFamilyOptionsEnabled,
    setOptionsAction: setFamilyOptions,
    initialOptions: familyOptions,
    resetKey: 'getFamilyOptions',
    filterUpdateKey: 'familyOptions',
    filterTypeKey: 'familyFilterType',
    timeFilterKey: 'familyOptionsTimeFilter',
    icon: (s) => <FamilyIcon className={s.family_icon} />,
    headerText: 'Family Options',
  },
  {
    key: 'professional',
    selector: 1,
    label: 'Personal',
    colorKey: 'personal',
    enabledSelector: selectProfressionalOptionsEnabled,
    setEnabledAction: setProfessionalOptionsEnabled,
    setOptionsAction: setProfessionalOptions,
    initialOptions: professionalOptions,
    resetKey: 'getProfessionalOptions',
    filterUpdateKey: 'professionalOptions',
    filterTypeKey: 'professionalFilterType',
    timeFilterKey: 'professionalOptionsTimeFilter',
    icon: (s) => <PersonalIcon className={s.professional_icon} />,
    headerText: 'Personal Options',
  },
  {
    key: 'organizational',
    selector: 2,
    label: 'Professional',
    colorKey: 'professional',
    enabledSelector: selectOrganizationalOptionsEnabled,
    setEnabledAction: setOrgOptionsEnabled,
    setOptionsAction: setOrganizationalOptions,
    initialOptions: organizationalOptions,
    resetKey: 'getOrganizationalOptions',
    filterUpdateKey: 'organizationalOptions',
    filterTypeKey: 'organizationalFilterType',
    timeFilterKey: 'organizationalOptionsTimeFilter',
    icon: (s) => <ProfessionalIcon className={s.organization_icon} />,
    headerText: 'Professional Options',
  },
  {
    key: 'charitable',
    selector: 3,
    label: 'Charitable',
    colorKey: 'charitable',
    enabledSelector: selectCharitableOptionsEnabled,
    setEnabledAction: setCharitableOptionsEnabled,
    setOptionsAction: setCharitableOptions,
    initialOptions: charitableOptions,
    resetKey: 'getCharitableOptions',
    filterUpdateKey: 'charitableOptions',
    filterTypeKey: 'charitableFilterType',
    timeFilterKey: 'charitableOptionsTimeFilter',
    icon: (s) => <CharitableIcon className={s.charitable_icon} />,
    headerText: 'Charitable Options',
  },
  {
    key: 'education',
    selector: 4,
    label: 'Education',
    colorKey: 'education',
    enabledSelector: selectEducationOptionsEnabled,
    setEnabledAction: setEducationOptionsEnabled,
    setOptionsAction: setEducationOptions,
    initialOptions: educationalOptions,
    resetKey: 'getEducationalOptions',
    filterUpdateKey: 'educationOptions',
    filterTypeKey: 'educationFilterType',
    timeFilterKey: 'educationOptionsTimeFilter',
    icon: (s) => <EducationIcon className={s.education_icon} />,
    headerText: 'Education Options',
  },
  {
    key: 'engagement',
    selector: 6,
    label: 'Engagement',
    colorKey: 'engagement',
    enabledSelector: selectEngagementOptionsEnabled,
    setEnabledAction: setEngagementOptionsEnabled,
    setOptionsAction: setEngagementOptions,
    initialOptions: engagementOptions,
    resetKey: 'getEngagementOptions',
    filterUpdateKey: 'engagementOptions',
    filterTypeKey: 'engagementFilterType',
    timeFilterKey: 'engagementOptionsTimeFilter',
    icon: () => <EngagementIcon />,
    headerText: 'Engagement Options',
  },
  // Hidden: Event nodes consume too many graph nodes
  // {
  //   key: 'attended',
  //   selector: 7,
  //   label: 'Attended',
  //   colorKey: 'attended',
  //   enabledSelector: selectAttendedOptionsEnabled,
  //   setEnabledAction: setAttendedOptionsEnabled,
  //   setOptionsAction: setAttendedOptions,
  //   initialOptions: attendedOptions,
  //   resetKey: 'getAttendedOptions',
  //   filterUpdateKey: 'attendedOptions',
  //   filterTypeKey: 'attendedFilterType',
  //   timeFilterKey: 'attendedOptionsTimeFilter',
  //   icon: () => <EventIcon />,
  //   headerText: 'Attended Options',
  // },
];

// Helper: applies view filter updates for a single category
const applyFilterUpdate = (viewFilterUpdates, fallbackTypes, category, currentOptions, resetOptions) => {
  const filterType = viewFilterUpdates[category.filterTypeKey];
  const optionValues = viewFilterUpdates[category.filterUpdateKey];

  if (filterType && optionValues?.length > 0) {
    const updated = { ...currentOptions };
    optionValues.forEach((key) => {
      if (updated[key]) {
        updated[key].isSelected = filterType || fallbackTypes[category.filterTypeKey];
      }
    });
    return updated;
  }
  return resetOptions[category.resetKey]();
};

const FilterCards = () => {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(null);
  const { ref, isVisible, setIsVisible } = useComponentVisible(false);
  const filterUpdates = useSelector(selectViewFilterUpdates);
  const nodeIsSelected = useSelector(selectNodeOptionsEnabled);
  const showVisible = useSelector(selectShowVisibleConnections);
  const timeFilters = useSelector(selectTimeFilters);
  const reset = useSelector(selectReset);
  const items = useSelector(selectStyledItems);
  const nodesDict = useSelector(selectNodes);
  const suggestedNodes = useSelector(selectSuggestedNodes);
  const currentDegree = useSelector(selectDegreesOfSeparation);

  // Category enabled selectors (must be called at top level per React rules)
  const categoryEnabledStates = {
    family: useSelector(selectFamilyOptionsEnabled),
    professional: useSelector(selectProfressionalOptionsEnabled),
    organizational: useSelector(selectOrganizationalOptionsEnabled),
    charitable: useSelector(selectCharitableOptionsEnabled),
    education: useSelector(selectEducationOptionsEnabled),
    engagement: useSelector(selectEngagementOptionsEnabled),
    attended: useSelector(selectAttendedOptionsEnabled),
  };

  const searchableNodes = useMemo(
    () => getSearchableNodes(items, nodesDict, suggestedNodes),
    [items, nodesDict, suggestedNodes],
  );

  const handleSearchSelect = (node) => {
    if (node.isHidden) {
      // Node is hidden by degree filter — expand to show all degrees, then focus
      dispatch(setDegreesOfSeparation(3));
      // Small delay so the chart re-renders with the node visible before focusing
      setTimeout(() => dispatch(setFocusedNode(node.id)), 100);
    } else {
      dispatch(setFocusedNode(node.id));
    }
  };

  // Category filter options state — one state object per category
  const [categoryOptions, setCategoryOptions] = useState(() => {
    const initial = {};
    FILTER_CATEGORIES.forEach((cat) => {
      initial[cat.key] = cat.initialOptions;
    });
    return initial;
  });

  // Time filters state — one state object for all categories
  const [categoryTimeFilters, setCategoryTimeFilters] = useState(() => {
    const initial = {};
    FILTER_CATEGORIES.forEach((cat) => {
      initial[cat.key] = timeFilters[cat.timeFilterKey];
    });
    return initial;
  });

  // Family-specific advanced state
  const [familyCustomOptions, setFamilyCustomOptions] = useState({
    ...customFamilyGroupingOptions,
  });
  const [familyAdvanced, setAdvancedFamily] = useState(false);

  // Node filters (separate from edge categories)
  const [nodeFilters, setNodeFilters] = useState(nodeOptions);

  const resetAllOptions = GetEdgeOptions();

  // --- Handlers ---
  const handleSelect = (selector) => {
    setSelected(selector);
    setIsVisible(true);
  };

  const close = () => {
    setSelected(null);
    setIsVisible(false);
  };

  // Generic dispatch handler for any category's filter options
  const dispatchCategoryFilters = useCallback(
    (actionCreator) =>
      ({ values, type, filterOut, timeFilter }) => {
        dispatch(actionCreator(values, type, filterOut, timeFilter));
      },
    [dispatch],
  );

  // Generic clear handler for any category
  const clearCategoryFilters = useCallback(
    (categoryKey, resetMethodName) => () => {
      setCategoryTimeFilters((prev) => ({
        ...prev,
        [categoryKey]: { start: null, end: null },
      }));
      const freshOptions = GetEdgeOptions();
      setCategoryOptions((prev) => ({
        ...prev,
        [categoryKey]: freshOptions[resetMethodName](),
      }));
      // Family-specific: also reset custom grouping options
      if (categoryKey === 'family') {
        setFamilyCustomOptions(customFamilyGroupingOptions);
      }
    },
    [],
  );

  // Set options for a specific category
  const setCategoryOption = useCallback(
    (categoryKey) => (newOptions) => {
      setCategoryOptions((prev) => ({
        ...prev,
        [categoryKey]: typeof newOptions === 'function' ? newOptions(prev[categoryKey]) : newOptions,
      }));
    },
    [],
  );

  // Node filter handlers
  const dispatchNodeFilters = ({ values, type, filterOut, timeFilter }) => {
    dispatch(setNodeOptionsFilter(values, type, filterOut, timeFilter));
  };

  const nodeFiltersEnabled = (value) => {
    dispatch(setNodeOptionsFilterEnabled(value));
  };

  const clearNodeFilters = () => {
    setNodeFilters(GetEdgeOptions().getNodeOptions());
  };

  // Visible connections toggle
  const visibleConnectionsStyle = showVisible
    ? { border: '1px solid #fff', background: '#000', color: '#fff' }
    : {};

  const toggleVisibleConnections = (e) => {
    e?.preventDefault();
    dispatch(setVisibleConnections(!showVisible));
  };

  // --- Effects ---

  // Sync filter updates from view changes
  useEffect(() => {
    const { viewFilterUpdates, ...types } = filterUpdates;
    const freshOptions = GetEdgeOptions();

    if (viewFilterUpdates !== undefined && viewFilterUpdates !== 'All') {
      // Apply updates for each category
      setCategoryOptions((prev) => {
        const next = { ...prev };
        FILTER_CATEGORIES.forEach((cat) => {
          next[cat.key] = applyFilterUpdate(
            viewFilterUpdates,
            types,
            cat,
            prev[cat.key],
            freshOptions,
          );
        });
        return next;
      });

      // Node options always update
      let newNodeOptions = { ...nodeOptions };
      viewFilterUpdates.nodeFilterOptions.forEach((element) => {
        if (newNodeOptions[element]) {
          newNodeOptions[element].isSelected =
            viewFilterUpdates.nodeFilterType || types.nodeFilterType;
        }
      });
      setNodeFilters(newNodeOptions);
    } else if (viewFilterUpdates === 'All') {
      // Reset all categories
      setCategoryOptions(() => {
        const reset = {};
        FILTER_CATEGORIES.forEach((cat) => {
          reset[cat.key] = freshOptions[cat.resetKey]();
        });
        return reset;
      });
      setNodeFilters(freshOptions.getNodeOptions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUpdates.viewFilterUpdates]);

  // Global reset
  useEffect(() => {
    if (reset !== 0) {
      const freshOptions = GetEdgeOptions();
      setCategoryOptions(() => {
        const resetState = {};
        FILTER_CATEGORIES.forEach((cat) => {
          resetState[cat.key] = freshOptions[cat.resetKey]();
        });
        return resetState;
      });
      setFamilyCustomOptions(customFamilyGroupingOptions);
      setNodeFilters(freshOptions.getNodeOptions());
    }
  }, [reset]);

  const showVisibleText = showVisible ? 'Show Orphaned' : 'Hide Orphaned';
  const colorKeys = Object.keys(edgeColors);

  return (
    <div className={styles.filter_cards_wrapper} ref={ref}>
      <div className={styles.filters}>
        {/* Node filter card */}
        <BaseFilterCard
          value="Nodes"
          isChecked={nodeIsSelected}
          setEnabled={nodeFiltersEnabled}
          setSelected={handleSelect}
          selector={5}
          isOpen={selected === 5 && isVisible}
          color="#000"
        />

        {/* Edge category filter cards — driven by FILTER_CATEGORIES */}
        {FILTER_CATEGORIES.map((cat) => (
          <BaseFilterCard
            key={cat.key}
            value={cat.label}
            isChecked={categoryEnabledStates[cat.key]}
            setEnabled={(value) => dispatch(cat.setEnabledAction(value))}
            setSelected={handleSelect}
            selector={cat.selector}
            isOpen={selected === cat.selector && isVisible}
            color={edgeColors[cat.colorKey]}
          />
        ))}

        <div
          data-tip
          data-for="visible-connections-button"
          className={styles.visible_wrapper}
          style={visibleConnectionsStyle}
          onClick={toggleVisibleConnections}
        >
          <AiOutlineAim />
        </div>
        <ReactTooltip
          id="visible-connections-button"
          place="top"
          effect="solid"
        >
          {showVisibleText}
        </ReactTooltip>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <GraphSearchControl
            searchableNodes={searchableNodes}
            onSelect={handleSearchSelect}
            placeholder="Find..."
          />
        </div>
      </div>

      {/* Node options panel */}
      {isVisible && selected === 5 && (
        <OptionsCardWrapper
          options={nodeFilters}
          setOptions={setNodeFilters}
          dispatchFilters={dispatchNodeFilters}
          close={close}
          headerText="Set Entity Type Filters"
          clear={clearNodeFilters}
          color="undefined"
        />
      )}

      {/* Edge category options panels — driven by FILTER_CATEGORIES */}
      {FILTER_CATEGORIES.map((cat) => {
        if (!(isVisible && selected === cat.selector)) return null;

        // Family has extra props for custom grouping
        const familyProps =
          cat.key === 'family'
            ? {
                setGroupOptions: setFamilyCustomOptions,
                customGrouping: familyCustomOptions,
                customGroupingMapping: FamilyGroupMapping,
                showAdvanced: familyAdvanced,
                setShowAdvanced: setAdvancedFamily,
              }
            : {};

        return (
          <OptionsCardWrapper
            key={cat.key}
            timeFilterWrapper={{
              timeFilter: categoryTimeFilters[cat.key],
              setTimeFilter: (tf) =>
                setCategoryTimeFilters((prev) => ({ ...prev, [cat.key]: tf })),
            }}
            options={categoryOptions[cat.key]}
            setOptions={setCategoryOption(cat.key)}
            dispatchFilters={dispatchCategoryFilters(cat.setOptionsAction)}
            close={close}
            icon={cat.icon(styles)}
            headerText={cat.headerText}
            clear={clearCategoryFilters(cat.key, cat.resetKey)}
            color={colorKeys[FILTER_CATEGORIES.indexOf(cat)]}
            {...familyProps}
          />
        );
      })}
    </div>
  );
};

export default FilterCards;
